import React, { useEffect } from 'react';
import { Routes, HashRouter, Route, useLocation, Outlet } from 'react-router-dom';
import { PrivateRoute, StandardRoutes } from '@sdir/auth/lib/components';
import { routes } from '../constants/routes';
import {
  CertificateOverviewPage,
  ApplicationPage,
  CertificatePage,
  RequirementsPage,
  AddFilesPage,
  ProfilePage,
  MissingDocumentationPage,
} from './Pages';
import {
  ContactInfo,
  SelectCertificate,
  AddCourse,
  Summary,
  Payment,
  AddSeagoing,
  AddAssessment,
  AddAdditionalCompetence,
  AddHealthCheck,
  AddForeignId,
  AddPhoto,
  AddSignature,
  AddNorwegianId,
} from './Organisms';
import { CurrentApplicationProvider, AddedQualificationsProvider } from './Providers';
import { QulificationsOpenStepsProvider } from './Providers/QulificationsOpenStepsProvider';
import { OwnCertificateProvider } from './Providers/OwnCertificateContext/OwnCertificateProvider';
import { AddCertificate } from './Organisms/NewAppStartFlow/AddCertificate/AddCertificate';
import { AddConfirmation } from './Organisms/NewAppStartFlow/AddConfirmation/AddConfirmation';
import { AddSeagoingNew } from './Organisms/NewAppStartFlow/AddSeagoingNew';
// eslint-disable-next-line max-len
import { AddSeagoingExternal } from './Organisms/NewAppStartFlow/AddSeagoingExternal/AddSeagoingExternal';
import { DEFAULT_LANG } from '../helpers/languageHelpers';
import { EndorsementPage } from './Pages/EndorsementPage';
import { OwnEndorsementProvider } from './Providers/OwnEndorsementProvider';
import { NewHomePage } from './Pages/HomePage/NewHomePage';
import { CompetenceDashboard } from './Pages/HomePage/CompetenceDashboard';
import ApplicationsOverviewPage from './Pages/ApplicationsOverviewPage';
import {
  CertReqRequirementsPage,
  QualificationRouteSwitch,
  certReqRequirementsRoute,
} from './Pages/CertReqRequirementsPage';
import { SidOverviewPage } from './Pages/SidOverviewPage';
import { NewSidApplicationPage } from './Pages/NewSidApplicationPage';

interface ApplicationProvidersProps {
  children?: any;
}

const ApplicationProviders: React.FC<ApplicationProvidersProps> = () => {
  return (
    <CurrentApplicationProvider>
      <QulificationsOpenStepsProvider>
        <AddedQualificationsProvider>
          <Outlet />
        </AddedQualificationsProvider>
      </QulificationsOpenStepsProvider>
    </CurrentApplicationProvider>
  );
};

type RoutesComponentProps = {
  currentLanguage: string;
  onChangeLanguage: (l: string, skipRefresh?: boolean) => void;
};

const RoutesComponent: React.FC<RoutesComponentProps> = ({ currentLanguage, onChangeLanguage }) => {
  const languagePrepend = currentLanguage === DEFAULT_LANG ? '' : `/${currentLanguage}`;
  return (
    <HashRouter>
      <OnRouteChanged />
      <Routes>
        <Route
          path={`${languagePrepend}${routes.home}`}
          element={
            <PrivateRoute>
              <NewHomePage onChangeLanguage={onChangeLanguage} />
            </PrivateRoute>
          }
        />
        <Route
          path={`${languagePrepend}${routes.competenceDashboard}`}
          element={
            <PrivateRoute>
              <CompetenceDashboard onChangeLanguage={onChangeLanguage} />
            </PrivateRoute>
          }
        />
        <Route
          path={`${languagePrepend}${routes.sidDashboard}`}
          element={
            <PrivateRoute>
              <SidOverviewPage />
            </PrivateRoute>
          }
        />
        <Route
          path={`${languagePrepend}${routes.newSidApplication}`}
          element={
            <PrivateRoute>
              <NewSidApplicationPage />
            </PrivateRoute>
          }
        />
        <Route
          path={`${languagePrepend}${routes.newApplication}`}
          element={
            <PrivateRoute>
              <OwnCertificateProvider>
                <SelectCertificate />
              </OwnCertificateProvider>
            </PrivateRoute>
          }
        />
        <Route element={<ApplicationProviders />}>
          <Route path={`${languagePrepend}${routes.applicationBase}`}>
            <Route
              path={`${languagePrepend}${routes.application.details}`}
              element={
                <PrivateRoute>
                  <ApplicationPage />
                </PrivateRoute>
              }
            />
            <Route
              path={`${languagePrepend}${routes.application.qualification}`}
              element={
                <PrivateRoute>
                  <QualificationRouteSwitch />
                </PrivateRoute>
              }
            />
            <Route
              path={`${languagePrepend}${certReqRequirementsRoute}`}
              element={
                <PrivateRoute>
                  <CertReqRequirementsPage />
                </PrivateRoute>
              }
            />
            <Route
              path={`${languagePrepend}${routes.application.addCourse}`}
              element={
                <PrivateRoute>
                  <AddCourse />
                </PrivateRoute>
              }
            />
            <Route
              path={`${languagePrepend}${routes.application.addSeagoing}`}
              element={
                <PrivateRoute>
                  <AddSeagoing />
                </PrivateRoute>
              }
            />
            <Route
              path={`${languagePrepend}${routes.application.addSeagoingAttachment}`}
              element={
                <PrivateRoute>
                  <AddSeagoingExternal />
                </PrivateRoute>
              }
            />
            <Route
              path={`${languagePrepend}${routes.application.addSeagoingNew}`}
              element={
                <PrivateRoute>
                  <AddSeagoingNew />
                </PrivateRoute>
              }
            />
            <Route
              path={`${languagePrepend}${routes.application.addCertificate}`}
              element={
                <PrivateRoute>
                  <AddCertificate />
                </PrivateRoute>
              }
            />
            <Route
              path={`${languagePrepend}${routes.application.addConfirmation}`}
              element={
                <PrivateRoute>
                  <AddConfirmation />
                </PrivateRoute>
              }
            />
            <Route
              path={`${languagePrepend}${routes.application.addAssessment}`}
              element={
                <PrivateRoute>
                  <AddAssessment />
                </PrivateRoute>
              }
            />
            <Route
              path={`${languagePrepend}${routes.application.addAdditionalCompetence}`}
              element={
                <PrivateRoute>
                  <AddAdditionalCompetence />
                </PrivateRoute>
              }
            />
            <Route
              path={`${languagePrepend}${routes.application.addHealthCheck}`}
              element={
                <PrivateRoute>
                  <AddHealthCheck />
                </PrivateRoute>
              }
            />
            <Route
              path={`${languagePrepend}${routes.application.addPhoto}`}
              element={
                <PrivateRoute>
                  <AddPhoto />
                </PrivateRoute>
              }
            />
            <Route
              path={`${languagePrepend}${routes.application.addSignature}`}
              element={
                <PrivateRoute>
                  <AddSignature />
                </PrivateRoute>
              }
            />
            <Route
              path={`${languagePrepend}${routes.application.addForeignId}`}
              element={
                <PrivateRoute>
                  <AddForeignId />
                </PrivateRoute>
              }
            />
            <Route
              path={`${languagePrepend}${routes.application.addNorwegianId}`}
              element={
                <PrivateRoute>
                  <AddNorwegianId />
                </PrivateRoute>
              }
            />
            <Route
              path={`${languagePrepend}${routes.application.editCourse}`}
              element={
                <PrivateRoute>
                  <AddCourse />
                </PrivateRoute>
              }
            />
            <Route
              path={`${languagePrepend}${routes.application.editSeagoing}`}
              element={
                <PrivateRoute>
                  <AddSeagoing />
                </PrivateRoute>
              }
            />
            <Route
              path={`${languagePrepend}${routes.application.contactInfo}`}
              element={
                <PrivateRoute>
                  <ContactInfo />
                </PrivateRoute>
              }
            />
            <Route
              path={`${languagePrepend}${routes.application.summary}`}
              element={
                <PrivateRoute>
                  <Summary />
                </PrivateRoute>
              }
            />
            <Route
              path={`${languagePrepend}${routes.application.payment}`}
              element={
                <PrivateRoute>
                  <Payment />
                </PrivateRoute>
              }
            />
            <Route
              path={`${languagePrepend}${routes.application.missingDocumentation}`}
              element={
                <PrivateRoute>
                  <RequirementsPage />
                </PrivateRoute>
              }
            />
            <Route
              path={`${languagePrepend}${routes.application.uploadMissingDocumentation}`}
              element={
                <PrivateRoute>
                  <MissingDocumentationPage />
                </PrivateRoute>
              }
            />
            <Route
              path={`${languagePrepend}${routes.application.addFiles}`}
              element={
                <PrivateRoute>
                  <AddFilesPage />
                </PrivateRoute>
              }
            />
          </Route>
        </Route>
        <Route
          path={`${languagePrepend}${routes.certificateOverview}`}
          element={
            <PrivateRoute>
              <OwnCertificateProvider>
                <OwnEndorsementProvider>
                  <CertificateOverviewPage />
                </OwnEndorsementProvider>
              </OwnCertificateProvider>
            </PrivateRoute>
          }
        />
        <Route
          path={`${languagePrepend}${routes.applicationsOverview}`}
          element={
            <PrivateRoute>
              <ApplicationsOverviewPage />
            </PrivateRoute>
          }
        />
        <Route
          path={`${languagePrepend}${routes.endorsements}`}
          element={
            <PrivateRoute>
              <OwnEndorsementProvider>
                <EndorsementPage />
              </OwnEndorsementProvider>
            </PrivateRoute>
          }
        />
        <Route
          path={`${languagePrepend}${routes.certificate}`}
          element={
            <PrivateRoute>
              <CertificatePage />
            </PrivateRoute>
          }
        />
        <Route
          path={`${languagePrepend}${routes.profile}`}
          element={
            <PrivateRoute>
              <ProfilePage />
            </PrivateRoute>
          }
        />
        <Route path="*" element={<StandardRoutes />} />
      </Routes>
    </HashRouter>
  );
};

const OnRouteChanged = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    document.documentElement.scrollTop = 0;
  }, [pathname]);

  return <></>;
};

export default RoutesComponent;
